<template>
  <div v-if="isUserValid" class="app-side-nav-wrapper">
    <div class="d-flex flex-column app-side-nav-container">
      <div v-if="!isPreferencesView" id="nav-container" class="mt-3 mb-auto">
        <v-expansion-panels v-model="expansionPanelStates" variant="accordion" multiple tile>
          <v-expansion-panel
            v-for="(cemodule, idx) in moduleItems"
            :id="'panel-' + cemodule.actionItem.id"
            :key="`expansion-panel-${idx}`"
            v-memo="moduleItems"
            class="side-nav"
            :value="idx"
          >
            <v-expansion-panel-title expand-icon="mdi-menu-down" collapse-icon="mdi-menu-up">
              <span class="ml-1 module-display-name">{{ cemodule.actionItem.displayName }}</span>
              <v-icon
                v-if="!isAdminDraftView"
                v-show="isShowSaveBtn(cemodule.actionItem.id)"
                :id="`app-side-nav-cancel-module-btn-${cemodule.actionItem.id}`"
                class="appSideNaveIconButton"
                :style="{
                  marginRight:
                    cemodule.updateItem != null &&
                    (isAllowedTo(4) || isAllowedTo(3) || isAllowedTo(2))
                      ? '18px'
                      : '10px'
                }"
                color="rgba(0, 0, 0, 0.54)"
                @click.stop="cancelConfirm(cemodule.actionItem.id)"
              >
                mdi-close-circle
              </v-icon>
              <v-icon
                v-if="!isAdminDraftView"
                v-show="isShowSaveBtn(cemodule.actionItem.id)"
                :id="`app-side-nav-save-module-btn-${cemodule.actionItem.id}`"
                :disabled="
                  loadingSaveVisibility || (loading && isShowSaveBtn(cemodule.actionItem.id))
                "
                class="appSideNaveIconButton"
                :style="{
                  marginRight:
                    cemodule.updateItem != null &&
                    (isAllowedTo(4) || isAllowedTo(3) || isAllowedTo(2))
                      ? '18px'
                      : '10px'
                }"
                color="rgba(0, 0, 0, 0.54)"
                @click.stop="saveConfirm(cemodule.actionItem.id)"
              >
                mdi-content-save
              </v-icon>
              <v-badge
                v-if="
                  !isAdminDraftView &&
                  cemodule.updateItem != null &&
                  (isAllowedTo(4) || isAllowedTo(3) || isAllowedTo(2))
                "
                :id="`has-pending-updates-badge-${cemodule.actionItem.id}`"
                inline
                color="rgb(var(--v-theme-secondary-darken-1))"
                class="has-pending-updates-badge"
              >
                <template #badge>
                  <v-btn
                    :id="`app-side-nav-update-module-btn-${cemodule.actionItem.id}`"
                    width="100%"
                    height="100%"
                    icon
                    variant="text"
                    size="small"
                    style="color: rgb(var(--v-theme-black))"
                    :ripple="false"
                    @click.stop="
                      showUpdateDetail(
                        cemodule.updateItem.summary,
                        cemodule.updateItem.publish.moduleId,
                        cemodule.actionItem.displayName
                      )
                    "
                    >{{ cemodule.updateItem.updateNumber }}
                  </v-btn>
                </template>
              </v-badge>
              <v-badge
                v-if="
                  isAdminDraftView &&
                  (cemodule.actionItem.clientId == client.id ||
                    cemodule.actionItem.tocVersion === TOC_VERSION_2) &&
                  cemodule.actionItem.hasUnpublishedDraftContent &&
                  (isAllowedTo(4) || isAllowedTo(3) || isAllowedTo(2))
                "
                :id="`has-unpublished-badge-${cemodule.actionItem.id}`"
                inline
                color="primary"
                class="has-unpublished-updates-badge"
              >
                <template #badge>
                  <v-btn
                    :id="`app-side-nav-update-module-btn-${cemodule.actionItem.id}`"
                    width="100%"
                    height="100%"
                    icon
                    variant="text"
                    size="small"
                    style="color: rgb(var(--v-theme-secondary))"
                    :ripple="false"
                    @click.stop="
                      showPublishDraftDialog(
                        cemodule.actionItem.id,
                        cemodule.actionItem.childActivated
                      )
                    "
                  >
                    1
                  </v-btn>
                </template>
              </v-badge>
            </v-expansion-panel-title>
            <v-expansion-panel-text eager>
              <v-card-text style="padding: 0">
                <toc
                  :id="'toc-' + cemodule.actionItem.id"
                  ref="tree"
                  :view-type="viewType"
                  :module-id="cemodule.actionItem.id"
                  :is-owned-by-client="moduleItemsOwnerStatus[cemodule.actionItem.id]"
                  :toc-version="cemodule.actionItem.tocVersion"
                  :is-expanded="expansionPanelStates.includes(idx)"
                  @page-selected="onPageSelected"
                  @change-show-save-btn="onChangeShowSaveBtn"
                  @page-changes="changingPages"
                  @set-state="setChildState"
                ></toc>
              </v-card-text>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <v-card v-if="isPreferencesView" id="nav-container-preferences" tile height="100%">
        <v-card class="preferences-panel" height="100%" dark>
          <div class="administration-panel">
            <strong>SETTINGS</strong>
          </div>
          <v-list class="pt-0">
            <v-list-item id="settings--context-library-tab" @click="showContextView">
              <template #prepend>
                <v-icon color="white">mdi-clipboard-account-outline</v-icon>
              </template>
              <v-list-item-title class="text-left">Context Library</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="isGroupAdmin"
              id="settings--group-publish-tab"
              @click="showGroupAdminView"
            >
              <template #prepend>
                <v-icon color="white">mdi-clipboard-arrow-down-outline</v-icon>
              </template>
              <v-list-item-title class="text-left">Group Publish</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="isAdmin"
              id="settings--content-admin-tab"
              @click="showContentAdminView"
            >
              <template #prepend>
                <v-icon color="white">mdi-view-list-outline</v-icon>
              </template>
              <v-list-item-title class="text-left">Content Admin</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isAdmin" id="settings--audit-log-tab" @click="showAuditLogView">
              <template #prepend>
                <v-icon color="white">mdi-book-outline</v-icon>
              </template>
              <v-list-item-title class="text-left">Audit Log</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="isAdmin"
              id="settings--report-dashboard-tab"
              @click="showReportDashboardView"
            >
              <template #prepend>
                <v-icon color="white">mdi-view-dashboard-outline</v-icon>
              </template>
              <v-list-item-title class="text-left">Report Dashboard</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="isAdmin && isAssuranceIntegrationEnabled"
              id="settings--assurance-integration-tab"
              @click="showAssuranceIntegrationView"
            >
              <template #prepend>
                <v-icon color="white">mdi-puzzle-outline</v-icon>
              </template>
              <v-list-item-title class="text-left">Assurance Integration</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="isAdmin"
              id="settings--content-register-tab"
              @click="showContentRegisterView"
            >
              <template #prepend>
                <v-icon color="white">mdi-file-table-box-multiple-outline</v-icon>
              </template>
              <v-list-item-title class="text-left">Content Register</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="isAdmin"
              id="settings--document-register-tab"
              @click="showDocumentRegisterView"
            >
              <template #prepend>
                <v-icon color="white">mdi-file-document-multiple-outline</v-icon>
              </template>
              <v-list-item-title class="text-left">Document Register</v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="isAdmin && isHubEnabled"
              id="settings--user-management-tab"
              @click="showUserManagementView"
            >
              <template #prepend>
                <v-icon color="white">mdi-account-cog-outline</v-icon>
              </template>
              <v-list-item-title class="text-left">User Management</v-list-item-title>
            </v-list-item>
            <!-- <v-list-item @click="showFeatureToggleView" v-if="isCSAdmin">
              <template v-slot:prepend>
                <v-icon color="white">mdi-view-module-outline</v-icon>
              </template>
              <v-list-item-content>
                <v-list-item-title class="text-left">Feature Toggle</v-list-item-title>
              </v-list-item-content>
            </v-list-item> -->
          </v-list>
        </v-card>
      </v-card>
      <div v-show="0" id="app-side-nav--policy-connect-version">
        {{ version }}
      </div>
    </div>
    <show-update-c-e-module-dialog
      v-model="showUpdateDialog"
      dialog-title="Update Module"
      :summary="summary"
      :file-diff="fileDiff"
      :publish-id="merge.publishId"
      :module-id="merge.moduleId"
      :module-display-name="merge.moduleDisplayName"
    ></show-update-c-e-module-dialog>
    <check-for-updates-message-dialog
      :message="message"
      :title="title"
      :show-dialog="showCheckUpdateMessageDialog"
      @closed="showCheckUpdateMessageDialog = false"
    ></check-for-updates-message-dialog>
    <publish-c-g-c-dialog
      :show-dialog="showPublishCGCDialog"
      :module-id="moduleId"
      :module-child-activated="moduleChildActivated"
      :is-draft-mode-enabled="isDraftModeEnabled"
      @closed="showPublishCGCDialog = false"
      @published="publishDraftAfter"
    ></publish-c-g-c-dialog>
  </div>
</template>

<script>
import _ from 'lodash';
import { unescape } from 'html-escaper';
import striptags from 'striptags';
import VueScrollTo from 'vue-scrollto';
import { mapState, mapGetters, mapMutations } from 'vuex';
import { plugInAdapter } from '@/lib/globalPlugIns';
import { CSBase } from '@complispace/cs-design-system';
import dataManager from '@/lib/engine';
import routerUtils from '@/lib/routerUtils';
import treeUtils from '@/lib/treeutils';
import * as types from '@/store/mutationTypes';
import { publish } from '@/api';
import EventBus from '@/bus';
import CEModuleTOC from '@/components/content/CEModuleTOC';
import markdownIt from 'markdown-it';
import { markdownItFancyListPlugin } from 'markdown-it-fancy-lists';
import packageParam from '../../../package.json';
import { getCurrentInstance } from 'vue';
import ShowUpdateCEModuleDialog from '../content/ShowUpdateCEModuleDialog';
import CheckForUpdatesMessageDialog from '../content/CheckForUpdatesMessageDialog';
import PublishCGCDialog from '../content/PublishCGCDialog';
import {
  PUBLISH_TYPE_NORMAL,
  ROLES,
  VIEW_TYPES,
  TOC_VERSION_2,
  UPDATE_CMD,
  PAGE_CHANGE_CMD,
  MODULE_UPDATE_TYPE,
  COMPONENT_STATE,
  COMPONENT_MANAGER_TYPE,
  MIXPANEL_TRACE_EVENT,
  MIXPANEL_TRACE_STRING
} from '../../constants';
import sessionManager from '../../store/modules/session/sessionManager';

const md = markdownIt({
  html: true
});

md.use(markdownItFancyListPlugin);

const instance = getCurrentInstance();

export default {
  name: 'AppSideNav',

  components: {
    toc: CEModuleTOC,
    ShowUpdateCEModuleDialog,
    CheckForUpdatesMessageDialog,
    PublishCGCDialog
  },

  extends: CSBase,

  data() {
    return {
      showUpdateDialog: false,
      showCheckUpdateMessageDialog: false,
      showPublishCGCDialog: false,
      tocs: [],
      tocActive: '',
      expansionPanelStates: [],
      moduleUpdates: [],
      fileDiff: {},
      summary: '',
      moduleItems: [],
      message: '',
      title: 'No updates available',
      moduleId: null,
      moduleChildActivated: false,
      merge: { publishId: -1, moduleId: -1 },
      TOC_VERSION_2,
      componentState: COMPONENT_STATE.STATE_IDLE,
      updateId: -1,
      changeFocus: {
        cmd: UPDATE_CMD.DEFAULT,
        values: {}
      },
      activatedCEModuleItems: null,
      buttonState: {},
      childStateManager: {},
      routeAvaiable: false,
      showingError: false,
      loadingSaveVisibility: false,
      isDraftModeEnabled: false
    };
  },

  computed: {
    ...mapState({
      app: (state) => state.app,
      isPreferencesView: (state) => state.appearance.isPreferencesView,
      navigation: (state) => state.navigation.navigation,
      client: (state) => state.client,
      subdomain: (state) => state.session.subdomain,
      // TODO: Uncomment to enable feature toggle functionality
      // featureToggle: state => state.authorisation.featureToggle,
      viewType: (state) => state.authorisation.viewType,
      tocState: (state) => state.data.tocState,
      moduleState: (state) => state.data.moduleState
    }),

    ...mapGetters({
      activatedCEModules: 'activatedCEModules/items',
      appliedUpdateItems: 'published/appliedItems',
      moduleAdmin: 'data/getAdminViewModules',
      moduleDraft: 'data/getDraftViewModules',
      contextItemValues: 'clientContext/values',
      isUserValid: 'authorisation/isUserValid',
      complispacePublishedItems: 'published/complispaceItems',
      isAllowedTo: 'authorisation/isAllowedTo',
      hasAccess: 'authorisation/hasAccess',
      isAdminDraftView: 'authorisation/isAdminDraftView',
      role: 'authorisation/role',
      getTOCMaster: 'data/getMasterTOC',
      getTOCDraft: 'data/getDraftTOC',
      getValidPageFromNav: 'navigation/getValidPageFromNav',
      isPageInNav: 'navigation/isPageInNav',
      isAssuranceIntegrationEnabled: 'client/isAssuranceIntegrationEnabled',
      appName: 'app/name',
      isHubEnabled: 'hub/isEnabled'
    }),

    // TODO: Uncomment to enable feature toggle functionality
    // We can use them to show and hide our WIP feature by using our feature toggle
    // showContentAdmin() {
    //     return this.featureToggle.clientModule;
    // },

    // showAuditLog() {
    //     return this.featureToggle.auditLog;
    // },

    unAcceptedPublish() {
      return this.appliedUpdateItems && this.appliedUpdateItems.length > 0
        ? this.appliedUpdateItems.filter((update) => update.typeId === PUBLISH_TYPE_NORMAL)
        : [];
    },

    version() {
      const { bundleVersion } = packageParam;
      const uiVersion = packageParam.version;
      const dashApiVersion = this.app.apiVersion ? `-${this.app.apiVersion}` : '';
      const contentEngineVersion = this.app.ceVersion ? `-${this.app.ceVersion}` : '';
      return `${bundleVersion}#${uiVersion}${dashApiVersion}${contentEngineVersion}`;
    },

    isEditing: {
      get() {
        let find = false;
        Object.keys(this.buttonState).forEach((moduleId) => {
          if (this.buttonState[moduleId]) {
            find = true;
          }
        });

        return find;
      }
    },

    isAdmin() {
      const roleId = parseInt(this.role, 10);
      if (roleId === 4 || roleId === 3 || roleId === 2) {
        return true;
      }
      return false;
    },

    isGroupAdmin() {
      const roleId = parseInt(this.role, 10);
      if (
        this.client.children &&
        this.client.children.length > 0 &&
        (roleId === 3 || roleId === 4)
      ) {
        return true;
      }
      return false;
    },

    isCSAdmin() {
      const roleId = parseInt(this.role, 10);
      if (roleId === 4) {
        return true;
      }
      return false;
    },

    isAdminClient() {
      return this.hasAccess('onoffaccessTable') || this.hasAccess('onoffmessageaccessTable');
    },

    moduleItemsOwnerStatus() {
      const ownerStatus = {};
      this.moduleItems.forEach((module) => {
        const { id } = module.actionItem;
        ownerStatus[id] = this.checkOwner(id);
      });
      return ownerStatus;
    }
  },

  watch: {
    $route: {
      handler(to) {
        if (to.name === 'Page') {
          const moduleId = parseInt(to.params.moduleId, 10);
          const path = to.params.pagePath;

          this.updateChild(
            moduleId,
            {
              cmd: UPDATE_CMD.SEARCH,
              values: {
                path,
                moduleId
              }
            },
            true
          );
        } else if (to.name === 'Dashboard') {
          this.moduleId = null;
          this.pagePath = '';
        }
      }
    },

    contextItemValues: {
      immediate: false,
      handler() {
        this.getTableOfContents();
        this.updateAllChild(false, false);
      }
    },

    tocState: {
      immediate: false,
      handler(newValue) {
        switch (newValue) {
          case dataManager.MANAGER_STATE.STATE_LOADING:
            this.setLoading(true);
            break;

          case dataManager.MANAGER_STATE.STATE_READY:
            {
              const comState = this.getComponentState();

              this.getTableOfContents();

              switch (comState) {
                case COMPONENT_STATE.STATE_GETTING_UPDATE:
                  this.setModuleActionItems();
                  this.showSuccessAlert('Success.');
                  break;

                case COMPONENT_STATE.STATE_PUBLISHING:
                  this.setModuleActionItems();
                  break;

                case COMPONENT_STATE.STATE_INITIALING:
                default:
                  this.initialModuleItems(this.viewType);
                  break;
              }

              this.updateChildHandler();
              this.setLoading(false);
              this.setComponentState(COMPONENT_STATE.STATE_IDLE);
            }
            break;

          default:
            break;
        }
      }
    },

    moduleState: {
      immediate: false,
      handler(newValue) {
        switch (newValue) {
          case dataManager.MANAGER_STATE.STATE_LOADING:
            break;

          case dataManager.MANAGER_STATE.STATE_READY:
            this.setLoading(true);
            this.setActiveModuleItems();
            this.getModuleUpdates();
            this.resetChildComponentManger();
            this.$store.dispatch('data/loadAllTOC', null);
            break;

          case dataManager.MANAGER_STATE.STATE_UPDATING:
            this.setLoading(true);
            if (this.getComponentState() !== COMPONENT_STATE.STATE_PUBLISHING) {
              this.setComponentState(COMPONENT_STATE.STATE_GETTING_UPDATE);
            }
            break;

          case dataManager.MANAGER_STATE.STATE_UPDATED:
            this.setActiveModuleItems();
            break;

          default:
            break;
        }
      }
    },

    async viewType(oldValue) {
      this.setLoading(true);
      this.setActiveModuleItems();
      this.getTableOfContents(true);
      this.initialModuleItems(oldValue);
      this.updateAllChild(false);
      this.setLoading(false);

      if (this.getComponentState() !== COMPONENT_STATE.STATE_INITIALING) {
        this.setComponentState(COMPONENT_STATE.STATE_IDLE);
      }
    },

    async role() {
      // Redirect to home page if the user is on the setting page and switch the role
      // to client view
      if (parseInt(this.role, 10) === ROLES.CLIENT_USER && this.isPreferencesView) {
        this.$router.push('/').catch(routerUtils.handleNavigationFailures);
        this.changePanelStates();
      }
    }
  },

  async mounted() {
    // TODO: Improvement - Try to find a better way rather than using EventBus
    EventBus.$on('onUpdateClientPage', (...data) => {
      this.changingPages(...data);
    });

    // When the user saves client module's draft content,
    // or creates a new page for client module, it will get triggered
    EventBus.$on('updateBadge', (moduleId) => {
      this.updateBadgeForClientModuleHelper(moduleId, true);
    });

    EventBus.$on('onContextKeyChangeFromPage', () => {
      this.getTableOfContents();
    });

    EventBus.$on('updateSideNavigation', () => {
      this.updateSideNav();
    });

    this.setComponentState(COMPONENT_STATE.STATE_INITIALING);

    if (this.tocState === COMPONENT_STATE.STATE_INITIALING && this.moduleItems.length === 0) {
      await this.$store.dispatch('data/loadAllTOC', null);
      this.setActiveModuleItems();
    }

    if (this.$store.getters['authorisation/isLoggedIn']) {
      try {
        sessionManager.setAdditionalHeaders();
        await this.$store.dispatch('published/getAllAppliedUpdates');
        await this.$store.dispatch('published/getAllComplispaceUpdates');
      } catch (error) {
        console.error(error);
      }
    }
  },

  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav);
  },

  beforeUnmount() {
    window.removeEventListener('beforeunload', this.preventNav);
  },

  methods: {
    ...mapMutations({
      setNavigation: types.NAVIGATION_SET_NAVIGATION,
      resetPageVisibilities: types.PAGE_VISIBILITIES_RESET_PAGE_VISIBILITIES,
      setInvisiblePages: types.PAGE_VISIBILITIES_SET_INVISIBLE_PAGES,
      setNavigationOver: types.NAVIGATION_SET_NAVIGATION_OVER
    }),

    getLatestPublishId(moduleId) {
      const updatesForModule = this.appliedUpdateItems.filter(
        (update) => update.moduleId === moduleId
      );

      if (updatesForModule && updatesForModule.length > 0) {
        const latestPublish = _.maxBy(updatesForModule, 'publishId');
        return latestPublish.publishId;
      }

      return null;
    },

    setChildState(values) {
      const { id: moduleId, type, value } = values;

      if (
        type === COMPONENT_MANAGER_TYPE.TYPE_COM_STATE &&
        value === COMPONENT_STATE.STATE_READY_FOR_DATA
      ) {
        const tocs = this.getTOC(moduleId);

        this.updateChild(moduleId, {
          cmd: UPDATE_CMD.DATACHANGE,
          values: {
            items: tocs.toc
          }
        });
      }
    },

    updateChildComponentManger(moduleId, type, state) {
      if (Object.prototype.hasOwnProperty.call(this.childStateManager, moduleId)) {
        this.childStateManager[moduleId][type] = state;
      }
    },

    resetChildComponentManger() {
      this.activatedCEModuleItems.forEach((ele) => {
        const state = {};

        state[COMPONENT_MANAGER_TYPE.TYPE_COM_STATE] = COMPONENT_STATE.STATE_INITIALING;
        this.childStateManager[ele.id] = state;
      });
    },

    checkOwner(moduleId) {
      const findModule = this.activatedCEModuleItems.find((ele) => ele.id === moduleId);
      if (!findModule) return false;
      return findModule.clientId === this.client.id;
    },

    setComponentState(state) {
      this.componentState = state;
    },

    getComponentState() {
      return this.componentState;
    },

    updateChildHandler() {
      const compState = this.getComponentState();
      switch (compState) {
        case COMPONENT_STATE.STATE_LOADING_DATA:
          if (this.updateId !== -1) {
            const tocs = this.getTOC(this.updateId);

            this.updateChild(this.updateId, {
              cmd: UPDATE_CMD.DATACHANGE,
              values: {
                items: tocs.toc
              }
            });
          }
          this.updateId = -1;
          break;

        case COMPONENT_STATE.STATE_INITIALING:
          this.updateAllChild(true);
          break;

        default:
          break;
      }
    },

    updateAllChild(showError, changeRoute = true) {
      if (this.moduleItems && this.moduleItems.length > 0) {
        instance?.proxy.$forceUpdate();

        let moduleId = -1;

        if (this.moduleId) {
          moduleId = this.moduleId;
        } else {
          moduleId = this.$route.params.moduleId;
        }

        if (moduleId) {
          const index = this.moduleItems.findIndex((ele) => ele.actionItem.id === moduleId);
          if (index === -1) {
            this.$router.push('/').catch(routerUtils.handleNavigationFailures);
            this.moduleId = -1;
            this.tocActive = '';
          }
        }

        this.$nextTick(() => {
          this.moduleItems.forEach((ele) => {
            const tocs = this.getTOC(ele.actionItem.id);

            if (tocs) {
              this.updateChild(
                ele.actionItem.id,
                {
                  cmd: UPDATE_CMD.DATACHANGE,
                  values: {
                    items: tocs.toc,
                    path: ele.actionItem.id === this.moduleId ? this.tocActive : '',
                    moduleId: this.moduleId
                  }
                },
                showError,
                changeRoute
              );
            }
          });
        });
      }
    },

    showContextView() {
      this.$router.push('/preferences/context').catch(routerUtils.handleNavigationFailures);
    },

    showContentAdminView() {
      this.$router.push('/preferences/contentAdmin').catch(routerUtils.handleNavigationFailures);
    },

    showGroupAdminView() {
      this.$router
        .push('/preferences/groupAdmin/processing-queue')
        .catch(routerUtils.handleNavigationFailures);
    },

    showReportDashboardView() {
      this.$router.push('/preferences/reportDashboard').catch(routerUtils.handleNavigationFailures);
    },

    showAssuranceIntegrationView() {
      this.$router
        .push('/preferences/assuranceIntegration')
        .catch(routerUtils.handleNavigationFailures);
    },

    showContentRegisterView() {
      this.$router.push('/preferences/contentRegister').catch(routerUtils.handleNavigationFailures);
    },

    showDocumentRegisterView() {
      this.$router
        .push('/preferences/documentRegister')
        .catch(routerUtils.handleNavigationFailures);
    },

    showUserManagementView() {
      this.$router.push('/preferences/userManagement').catch(routerUtils.handleNavigationFailures);
    },

    // TODO: Uncomment to enable feature toggle functionality
    // showFeatureToggleView() {
    //     this.$router.push('/preferences/featureToggle')
    //          .catch(routerUtils.handleNavigationFailures);
    // },

    showAuditLogView() {
      this.$router.push('/preferences/auditLog').catch(routerUtils.handleNavigationFailures);
    },

    showPublishDraftDialog(moduleId, moduleChildActivated) {
      this.showPublishCGCDialog = true;
      this.moduleId = moduleId;
      this.moduleChildActivated = moduleChildActivated;
      this.isDraftModeEnabled = true;
    },

    getTOC(moduleId) {
      let res = null;

      if (this.tocs && this.tocs.length > 0) {
        res = this.tocs.find((toc) => toc.moduleId === moduleId);
      }
      return res;
    },

    animateTOC(moduleId, pagePath) {
      // Open the expansion panel for appropriate panel
      // Scroll to options
      const options = {
        container: '#navDraw',
        duration: 500,
        easing: 'ease',
        offset: -60,
        force: true,
        cancelable: true,
        onStart: false,
        onDone: false,
        onCancel: false,
        x: false,
        y: true
      };

      // Scroll to current heading
      // TODO: Can't use setTimeout
      // Need to wait for expansion panel to expand and tree to create
      // the nodes in the DOM.
      setTimeout(() => {
        VueScrollTo.scrollTo(
          `#toc-node-${moduleId}-${pagePath.substring(0, pagePath.length - 3)}`,
          250,
          options
        );
      }, 2000);
    },

    onPageSelected(details) {
      if (details && details.path) {
        this.tocActive = details.path;
        this.moduleId = details.moduleId;
        const tocRefs = this.getTocRefs();
        if (tocRefs.length > 0) {
          tocRefs.forEach((tree) => {
            if (tree.moduleId !== this.moduleId) {
              this.updateChild(tree.moduleId, {
                cmd: UPDATE_CMD.LOSEFOCUS,
                values: {}
              });
            }
          });
        }

        this.changePanelStates(this.moduleId);
      }
    },

    updateDataHandler(moduleId, values) {
      if (Object.prototype.hasOwnProperty.call(values, 'unPublish')) {
        const { unPublish } = values;
        this.$store.dispatch('data/updateModuleProp', {
          id: moduleId,
          type: MODULE_UPDATE_TYPE.TYPE_HAS_UNPUBLISH_CONTENT,
          value: unPublish
        });
      }

      if (Object.prototype.hasOwnProperty.call(values, 'reload')) {
        const { reload } = values;

        if (reload) {
          this.$store.dispatch('data/loadSingleTOC', moduleId);
          this.updateId = moduleId;
          this.setComponentState(COMPONENT_STATE.STATE_LOADING_DATA);
        }
      }
    },

    onPageChanged(params) {
      const { id: moduleId, cmd, values } = params;

      switch (cmd) {
        case PAGE_CHANGE_CMD.UPDATE_DATA:
          this.updateDataHandler(moduleId, values);
          break;

        default:
          break;
      }
    },

    getTableOfContents() {
      this.tocs = this.getGroupTOCS();
      this.setNavigation(this.tocs);
      this.setNavigationOver(true);
    },

    async getUpdate(isCheckUpdate) {
      if (this.loading) return;
      const comState = this.getComponentState();
      this.setLoading(true);
      try {
        this.setComponentState(COMPONENT_STATE.STATE_LOADING_DATA);
        await this.$store.dispatch('published/getAllAppliedUpdates');
        await this.$store.dispatch('published/getAllComplispaceUpdates');
        this.trackMixPanelPendingUpdatesEvent();

        this.getModuleUpdates();
        if (this.viewType === VIEW_TYPES.ADMIN_PUBLISH) {
          this.setUpdateModuleItems();
        }
        if (isCheckUpdate) {
          if (Object.keys(this.moduleUpdates).length > 0) {
            this.title = 'Updates available';
            this.message = 'These are now visible in your sidebar module list.';
          } else {
            this.title = 'No updates available';
            this.message =
              'When updates are available they will be visible on your sidebar module list.';
          }
          this.showCheckUpdateMessageDialog = true;
        }
      } catch (error) {
        this.showErrorResponseAlert(error);
      } finally {
        this.setLoading(false);
        this.setComponentState(comState);
      }
    },
    trackMixPanelPendingUpdatesEvent() {
      try {
        if (this.appliedUpdateItems && this.appliedUpdateItems.length > 0) {
          const { plugIns } = this.$app;

          const commonMixpanelProperties = {
            appName: this.appName,
            client: this.client,
            subdomain: this.subdomain,
            userRole: MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.ADMIN
          };

          this.appliedUpdateItems.forEach((appliedUpdatedItem) => {
            const foundModule = this.activatedCEModules.find(
              (activatedCEModules) => activatedCEModules.id === appliedUpdatedItem.moduleId
            );

            const update = appliedUpdatedItem;
            if (
              update.typeId === PUBLISH_TYPE_NORMAL &&
              (foundModule.clientId === null || foundModule.clientId === undefined)
            ) {
              this.$store.dispatch('published/setPublishAt', {
                id: appliedUpdatedItem.publishId,
                publishAt: update.createdAt
              });
              const pendingUpdatesProperties = {
                publishId: update.publishId,
                publishSummary: update.publishSummary,
                typeId: update.typeId,
                publishAt: update.publishAt,
                moduleId: update.moduleId,
                moduleName: update.moduleName
              };
              const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
                ...commonMixpanelProperties,
                ...pendingUpdatesProperties
              });

              plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.PENDING_UPDATES, properties);
            }
          });
        }
      } catch (error) {
        console.error('Mixpanel Error: ', error);
      }
    },

    setModuleActionItems() {
      if (this.activatedCEModuleItems && this.activatedCEModuleItems.length > 0) {
        this.activatedCEModuleItems.forEach((cemodule) => {
          const isCeModuleVisible = _.some(
            this.tocs,
            (it) => it.moduleId === cemodule.id && !!it.toc
          );

          if (isCeModuleVisible) {
            const newItem = _.extend({}, cemodule, {
              displayName: unescape(striptags(cemodule.displayName))
            });

            const findIdx = this.moduleItems.findIndex((ele) => {
              return ele.actionItem.id === cemodule.id;
            });
            if (findIdx === -1) {
              this.moduleItems.push({ actionItem: newItem });
            } else {
              this.moduleItems[findIdx].actionItem = newItem;
            }
          }
        });
      }
    },

    initialModuleItems(viewType) {
      this.moduleItems = [];

      this.setModuleActionItems();
      this.setUpdateModuleItems();
      if (viewType === VIEW_TYPES.ADMIN_PUBLISH) {
        this.initialButtonState();
      }
    },

    getUpdateItem(moduleId, updateObj) {
      let res = null;
      if (updateObj) {
        const findKey = Object.keys(this.moduleUpdates).find(
          (key) => this.moduleUpdates[key].publish.moduleId === moduleId
        );
        if (findKey) {
          res = this.moduleUpdates[findKey];
        }
      }

      return res;
    },

    setUpdateModuleItems() {
      if (this.moduleItems.length > 0) {
        let updateItem = null;

        for (let i = 0; i < this.moduleItems.length; i += 1) {
          updateItem = this.getUpdateItem(this.moduleItems[i].actionItem.id, this.moduleUpdates);
          if (updateItem) {
            this.moduleItems[i].updateItem = updateItem;
          } else if (Object.prototype.hasOwnProperty.call(this.moduleItems[i], 'updateItem')) {
            this.moduleItems[i].updateItem = null;
            delete this.moduleItems[i].updateItem;
          }
        }
      }
    },

    getModuleUpdates() {
      // Add module name to each update entry
      const mapped = this.unAcceptedPublish.map((item) => {
        const mappedItem = { ...item };
        const foundModule = _.chain(this.activatedCEModuleItems)
          .filter((cemoduleItem) => cemoduleItem.id === item.moduleId)
          .first()
          .value();
        if (foundModule) {
          mappedItem.moduleName = foundModule.name;
        }
        return mappedItem;
      });

      // Group the latest updates my module
      const csGroupedByModule = _.groupBy(mapped, 'moduleName');

      Object.keys(csGroupedByModule).forEach((key) => {
        const items = csGroupedByModule[key];
        const latestPublished = _.maxBy(items, 'publishId');

        // Convert the markdown summaries into html
        const summary = items.reduce((text, result) => {
          return `${text}<br/><br/><h4 class="primary--text">
                        ${new Date(result.createdAt)}</h4>
                        <br/>${md.render(result.publishSummary)}`;
        }, '');

        csGroupedByModule[key] = {
          publish: latestPublished,
          updateNumber: items.length,
          summary
        };
      });

      this.moduleUpdates = csGroupedByModule;
    },

    async showUpdateDetail(summary, moduleId, moduleDisplayName) {
      if (!this.hasAccess('processupdatesaccessTable')) {
        return;
      }
      this.setLoading(true);
      try {
        // silent publish include
        const publishId = this.getLatestPublishId(moduleId);
        if (publishId) {
          const response = await this.getDiffDate(publishId);
          this.fileDiff = response.data;
          this.summary = summary;
          this.merge.publishId = publishId;
          this.merge.moduleId = moduleId;
          this.merge.moduleDisplayName = unescape(striptags(moduleDisplayName));
          this.showUpdateDialog = true;
        }
      } catch (err) {
        this.showErrorResponseAlert(err);
      } finally {
        this.setLoading(false);
      }
    },

    async getDiffDate(publishId) {
      return publish.getFileDifferences(publishId);
    },

    updateSideNav() {
      if (this.viewType === VIEW_TYPES.ADMIN_PUBLISH) {
        this.getModuleUpdates();
        this.setUpdateModuleItems();
      }

      const params = {
        id: this.merge.moduleId,
        cmd: PAGE_CHANGE_CMD.UPDATE_DATA,
        values: {
          reload: true
        }
      };

      this.onPageChanged(params);
    },

    treeUpdate(details) {
      this.updateChild(details.moduleId, details.values);
    },

    changePanelStates(moduleId) {
      const currentModuleItemIndex = this.moduleItems.findIndex(
        (item) => item.actionItem.id === moduleId
      );
      this.expansionPanelStates = currentModuleItemIndex > -1 ? [currentModuleItemIndex] : [];
    },

    cancelConfirm(moduleId) {
      const tocs = this.getTOC(moduleId);
      this.updateChild(moduleId, {
        cmd: UPDATE_CMD.REFRESH,
        values: {
          items: tocs.toc
        }
      });
      // to hide the cancel and save button
      this.setSingleButtonState(moduleId, !this.getButtonState(moduleId));
    },

    async saveConfirm(moduleId) {
      this.loadingSaveVisibility = true;
      const tocRefs = this.getTocRefs();
      const moduleTOCTree = tocRefs.find((toc) => toc.moduleId === moduleId);
      await moduleTOCTree.saveConfirm();
      this.loadingSaveVisibility = false;
    },

    onChangeShowSaveBtn(details) {
      this.setSingleButtonState(details.moduleId, details.value);

      if (this.isEditing) {
        this.setComponentState(COMPONENT_STATE.STATE_EDITING);
      } else {
        this.setComponentState(COMPONENT_STATE.STATE_IDLE);
      }
    },

    preventNav(event) {
      const checkUrl = this.$route.path.indexOf('/module/') > -1 || this.$route.path === '/';

      if (checkUrl && this.isEditing === true) {
        event.preventDefault();

        // eslint-disable-next-line no-param-reassign
        event.returnValue = 'Do you really want to leave?\nChanges that you made may not be saved.';
      }
    },

    checkModuleInItems(moduleId) {
      let res = false;
      const admin = this.moduleAdmin();

      if (admin.findIndex((ele) => ele.id === moduleId) === -1) {
        res = false;
      } else {
        res = true;
      }
      return res;
    },

    publishDraftAfter(publishedModuleId) {
      if (!this.checkModuleInItems(publishedModuleId)) {
        this.setComponentState(COMPONENT_STATE.STATE_PUBLISHING);
        this.$store.dispatch('activatedCEModules/getAll', publishedModuleId);
        this.$store.commit(types.DATA_SET_MODULE_STATE, dataManager.MANAGER_STATE.STATE_UPDATING);
      } else {
        this.updateBadgeForClientModuleHelper(publishedModuleId, false);
      }
    },

    changingPages(values) {
      this.onPageChanged(values);
      this.setModuleActionItems();
    },

    updateBadgeForClientModuleHelper(moduleId, hasUnpublishedDraftContent) {
      this.onPageChanged({
        id: moduleId,
        cmd: PAGE_CHANGE_CMD.UPDATE_DATA,
        values: {
          reload: true,
          unPublish: hasUnpublishedDraftContent
        }
      });

      this.setModuleActionItems();
    },

    getSingleTOC(moduleId) {
      let res = null;

      if (this.viewType === VIEW_TYPES.ADMIN_DRAFT) {
        res = this.getTOCDraft(moduleId, this.contextItemValues, this.viewType);
      } else {
        res = this.getTOCMaster(moduleId, this.contextItemValues, this.viewType);
      }

      return res;
    },

    getGroupTOCS() {
      const res = [];
      const { parentId } = this.client;
      let filter = (node) => node.visible; // used for client user
      // Admins get all nodes regardless of visibility
      if (this.isAllowedTo(4) || this.isAllowedTo(3) || this.isAllowedTo(2)) {
        // child site
        if (parentId) {
          filter = (node) => node.visible;
        } else {
          filter = () => true;
        }
      }

      if (this.activatedCEModuleItems && this.activatedCEModuleItems.length > 0) {
        this.activatedCEModuleItems.forEach((ele) => {
          const toc = this.getSingleTOC(ele.id);
          if (toc) {
            res.push({
              moduleId: ele.id,
              toc: treeUtils.filterTree(toc, filter)
            });
          }
        });
      }

      return res;
    },

    resetFocusCmd() {
      this.changeFocus = {
        cmd: UPDATE_CMD.DEFAULT,
        values: {}
      };
    },

    changeRoute(page) {
      if (page === '/') {
        this.$router.push('/').catch(routerUtils.handleNavigationFailures);
      } else {
        this.$router
          .push({
            name: 'Page',
            params: {
              moduleId: this.moduleId,
              pagePath: page
            }
          })
          .catch(routerUtils.handleNavigationFailures);
      }
    },

    getTocRefs() {
      return this.$refs?.tree ?? [];
    },

    updateChild(moduleId, focusCmd, showError = false, changeRoute = true) {
      const tocRefs = this.getTocRefs();
      if (tocRefs && tocRefs.length > 0) {
        const child = tocRefs.find((ele) => ele.moduleId === moduleId);

        if (focusCmd != null) {
          this.changeFocus = focusCmd;
        } else {
          this.resetFocusCmd();
        }

        // TODO: the title filter probably not used anymore, needs to do investigation
        // Unescape page titles
        if (this.changeFocus.values && this.changeFocus.values.items) {
          this.changeFocus.values.items.name = unescape(
            striptags(this.changeFocus.values.items.name)
          );

          const unescapePageTitle = (page) => {
            if (page.children && page.children.length) {
              page.children.forEach((childPage) => {
                // eslint-disable-next-line no-param-reassign
                childPage.name = unescape(striptags(childPage.name));
                unescapePageTitle(childPage);
              });
            }
          };

          unescapePageTitle(this.changeFocus.values.items);
        }

        if (child) {
          switch (this.changeFocus.cmd) {
            case UPDATE_CMD.SEARCH:
              if (!this.tocActive) {
                child.getUpdateFromParent(this.changeFocus.cmd, this.changeFocus.values);
              } else {
                const page = this.getValidPageFromNav(moduleId, this.changeFocus.values.path);

                if (page !== this.changeFocus.values.path) {
                  this.showingError = true;
                  if (changeRoute) this.changeRoute(this.tocActive);
                } else {
                  if (this.showingError) {
                    this.changeFocus.cmd = UPDATE_CMD.SHOWINGERROR;
                    this.showingError = false;
                  }

                  child.getUpdateFromParent(this.changeFocus.cmd, this.changeFocus.values);
                }
              }
              break;

            case UPDATE_CMD.DATACHANGE:
              if (
                Object.prototype.hasOwnProperty.call(this.changeFocus.values, 'path') &&
                this.changeFocus.values.path
              ) {
                const page = this.getValidPageFromNav(moduleId, this.changeFocus.values.path);

                if (this.tocActive) {
                  if (page !== this.tocActive) {
                    if (showError) {
                      this.changeFocus.cmd = UPDATE_CMD.SHOWINGERROR;
                    } else {
                      if (changeRoute) this.changeRoute(page);
                      if (page !== '/') {
                        this.tocActive = page;
                      }
                    }
                  }
                }
              } else if (showError) {
                if (!this.tocActive) {
                  const { pagePath } = this.$route.params;
                  if (pagePath) {
                    const page = this.getValidPageFromNav(moduleId, pagePath);
                    if (page !== pagePath) {
                      this.changeFocus.cmd = UPDATE_CMD.SHOWINGERROR;
                    }
                  }
                }
              }

              child.getUpdateFromParent(this.changeFocus.cmd, this.changeFocus.values);

              break;

            default:
              child.getUpdateFromParent(this.changeFocus.cmd, this.changeFocus.values);
              break;
          }
        } else if (this.moduleId && this.changeFocus.values.path) {
          if (!this.showingError) {
            this.showingError = true;
            if (changeRoute) this.changeRoute(this.tocActive);
          }
        }
      } else if (this.moduleId && this.tocActive) {
        const isPageAccessible = this.isPageInNav(this.moduleId, this.tocActive);
        if (isPageAccessible) {
          if (changeRoute) this.changeRoute(this.tocActive);
        }
      }
    },

    setActiveModuleItems() {
      if (this.viewType === VIEW_TYPES.ADMIN_DRAFT) {
        this.activatedCEModuleItems = this.moduleDraft();
      } else {
        this.activatedCEModuleItems = this.moduleAdmin();
      }
    },

    setSingleButtonState(moduleId, state) {
      if (Object.prototype.hasOwnProperty.call(this.buttonState, moduleId)) {
        this.buttonState[moduleId] = state;
      } else {
        this.buttonState[moduleId] = state;
      }
    },

    initialButtonState() {
      this.moduleItems.forEach((ele) => {
        this.setSingleButtonState(ele.actionItem.id, false);
      });
    },

    getButtonState(moduleId) {
      if (Object.prototype.hasOwnProperty.call(this.buttonState, moduleId)) {
        return this.buttonState[moduleId];
      }
      return false;
    },

    isShowSaveBtn(moduleId) {
      let res = false;

      if (this.isAdminClient && this.getButtonState(moduleId)) {
        res = true;
      }

      return res;
    },

    // this is from parent component
    updateComponent(type, value) {
      switch (type) {
        case COMPONENT_MANAGER_TYPE.TYPE_COM_STATE:
          {
            const { state: comState } = value;
            if (comState === COMPONENT_STATE.STATE_IDLE) {
              this.initialButtonState();
              this.setComponentState(COMPONENT_STATE.STATE_IDLE);
            }
          }
          break;

        default:
          break;
      }
    }
  }
};
</script>

<style scoped>
.app-side-nav-container {
  height: 100%;
}

.module-display-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0.1rem 0;
}

#nav-container .v-badge {
  flex: none;
}

#nav-container .v-badge__badge {
  padding: 0px;
}

#nav-container :deep(.has-unpublished-updates-badge .v-badge__badge .v-btn .v-btn__content) {
  color: #ffffff;
  font-size: 12px;
  font-weight: 700 !important;
}

#nav-container :deep(.has-unpublished-updates-badge .v-badge__badge) {
  display: flex;
  justify-content: center;
  align-items: center;
}

#nav-container :deep(.has-pending-updates-badge .v-badge__badge .v-btn .v-btn__content) {
  color: rgb(var(--v-theme-black));
  font-size: 12px;
  font-weight: 700 !important;
}

#nav-container :deep(.has-pending-updates-badge .v-badge__badge) {
  display: flex;
  justify-content: center;
  align-items: center;
}

#nav-container :deep(.v-expansion-panel-title__icon .v-icon) {
  color: rgba(0, 0, 0, 0.54) !important;
}

#nav-container :deep(.v-expansion-panel-title__overlay) {
  background-color: #ffffff;
}

.ml-1 {
  width: 90% !important;
}

#nav-container-preferences .preferences-panel {
  padding: 40px;
  background-color: rgb(var(--v-theme-primary-darken-1));
}

#nav-container-preferences .v-list-item__action {
  margin-right: 12px;
}

#nav-container-preferences .administration-panel {
  font-size: 1rem;
  padding-top: 24px;
  margin-bottom: 10px;
  margin-left: 16px;
  color: #ffffff;
}

#nav-container .v-expansion-panel-title {
  padding: 0 12px;
}

.appSideNaveIconButton {
  max-width: 28px;
  margin-right: 20px;
}

.app-side-nav-wrapper {
  height: 100%;
}

.v-list-item {
  min-height: 48px;
}

.text-left {
  color: #ffffff;
}
.v-list-item__prepend .v-icon {
  opacity: 1;
}
</style>
